import React, { useEffect, Fragment } from 'react';
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Box,
  Button,
  Paper,
  List,
  Grid,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import Spinner from '../Spinner/Spinner';
import { ICourse } from '../../interfaces/ICourse';
import SearchBar from '../SearchBar/SearchBar';

const useStyles = makeStyles(( theme: Theme ) => createStyles({
  root: {
    margin: 'auto',
  },
  paper: {
    minHeight: 300,
    minWidth: 200,
    height: 230,
    overflow: 'auto',
  },
  button: {
    margin: theme.spacing( 0.5, 0 ),
  },
}));

interface ITransferListProps {
  // eslint-disable-next-line react/require-default-props
  itemsRIGHT?: ICourse;
  // eslint-disable-next-line react/require-default-props
  itemsLEFT?: any[];
  // eslint-disable-next-line react/require-default-props
  type?: number;
  handleSaveData: any;
  handleDeleteData: any;
  // eslint-disable-next-line react/require-default-props
  loading?: string;
}

function not( a: string[], b: string[]): string[] {
  return a.filter(( value ) => b.indexOf( value ) === -1 );
}

function intersection( a: string[], b: string[]): string[] {
  return a.filter(( value ) => b.indexOf( value ) !== -1 );
}

const TransferList: React.FC<ITransferListProps> = ( props ) => {
  const {
    itemsRIGHT,
    itemsLEFT = [],
    type,
    handleSaveData,
    handleDeleteData,
    loading,
  } = props;

  const classes = useStyles();
  const [checked, setChecked] = React.useState<string[]>([]);
  const [left, setLeft] = React.useState<string[]>([]);
  const [right, setRight] = React.useState<string[]>([]);
  const [input, setInput] = React.useState( '' );

  const leftChecked = intersection( checked, left );
  const rightChecked = intersection( checked, right );

  const filterBooks = ( data: string ): void => {
    const bookFound = itemsLEFT.filter(
      ( item: any ) => item.name.toLowerCase().includes( data.toLowerCase()),
    );
    if ( bookFound.length > 0 ) {
      const onlyIds = bookFound.map(( item: any ) => item.id );
      const items = onlyIds.filter(
        ( item: any ) => !right.includes( item ),
      );
      setLeft( items );
    } else {
      const onlyIds = itemsLEFT.map(( item: any ) => item.id );
      const items = onlyIds.filter(
        ( item: any ) => !right.includes( item ),
      );
      setLeft( items );
    }
    setInput( data );
  };

  useEffect(() => {
    const onlyIds = itemsLEFT.map(( item: any ) => item.id );
    const rightItems = itemsRIGHT?.subCollectionsItems?.map((
      item: any,
    ) => item.id ) || [];
    const leftItems = onlyIds.filter(
      ( item: any ) => !rightItems.includes( item ),
    );
    setLeft( leftItems );
    setRight( rightItems );
    setChecked([]);
  }, [props]);

  const handleGetNameLeft = ( id: string ): string => {
    const userName = itemsLEFT.find(( x: any ) => x.id === id );
    return userName ? `${userName.name} ${userName.surname || ''}` : '';
  };

  const handleToggle = ( value: string ) => () => {
    const currentIndex = checked.indexOf( value );
    const newChecked = [...checked];

    if ( currentIndex === -1 ) {
      newChecked.push( value );
    } else {
      newChecked.splice( currentIndex, 1 );
    }
    setChecked( newChecked );
  };

  const handleCheckedRight = (): void => {
    setRight( right.concat( leftChecked ));
    setLeft( not( left, leftChecked ));
    setChecked( not( checked, leftChecked ));
    if ( leftChecked.length > 0 ) {
      const arrayToSave = handleCreateArray( leftChecked );
      handleSaveData( type, arrayToSave );
    }
  };

  const handleCreateArray = ( items: any[]): any[] => {
    const arrayResult = [];
    for ( let i = 0; i < items.length; i++ ) {
      const itemData: any = itemsLEFT.find((
        item: any,
      ) => item.id === items[i]);
      if ( itemData ) {
        const { id, name, surname } = itemData;
        arrayResult.push({ id, name: `${name} ${surname}` });
      }
    }
    return arrayResult;
  };

  const handleCheckedLeft = (): void => {
    setLeft( left.concat( rightChecked ));
    setRight( not( right, rightChecked ));
    setChecked( not( checked, rightChecked ));
    handleDeleteData( type, rightChecked );
  };

  const customList = ( items: any ): any => (
    <Paper className={classes.paper}>
      <List dense component="div" role="list">
        {items.map(( value: string ) => {
          const labelId = `transfer-list-item-${value}-label`;
          return (
            <ListItem
              key={value.toString()}
              role="listitem"
              button
              onClick={handleToggle( value )}
            >
              <ListItemIcon>
                <Checkbox
                  checked={checked.indexOf( value ) !== -1}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ 'aria-labelledby': labelId }}
                />
              </ListItemIcon>
              <ListItemText id={labelId} primary={handleGetNameLeft( value )} />
            </ListItem>
          );
        })}
        <ListItem />
      </List>
    </Paper>
  );

  return (
    <Grid
      container
      spacing={2}
      justify="center"
      alignItems="center"
      className={classes.root}
    >
      <Grid item xl={12} sm={12}>
        <SearchBar keyword={input} setKeyword={filterBooks} />
      </Grid>
      <Grid item xl={12} sm={5}>{customList( left )}</Grid>
      <Grid item xl={12} sm={2}>
        <Grid container direction="column" alignItems="center">
          <Button
            variant="outlined"
            size="small"
            className={classes.button}
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
            aria-label="move selected right"
          >
            &gt;
          </Button>
          <Button
            variant="outlined"
            size="small"
            className={classes.button}
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0}
            aria-label="move selected left"
          >
            &lt;
          </Button>
        </Grid>
      </Grid>
      <Grid item xl={12} sm={5}>
        {loading
          ? (
            <>
              <Spinner />
            </>
          )
          : (
            <>
              { right.length > 0 ? (
                customList( right )
              )
                : (
                  <Box textAlign="center">
                    <span>Sin datos...</span>
                  </Box>
                )}
            </>
          )}
      </Grid>
    </Grid>
  );
};

export default TransferList;
